import _ from 'lodash'
import moment from 'moment'
import numeral from 'numeral'

export const defaultPlan = {
  code: 'free',
  product: 'Free',
  interval: 'month',
  nickname: ''
}

export const defaultTrialEnd = () => {
  const trialEnd = moment().utc()
  trialEnd.add(30, 'days') // eslint-disable-line no-magic-numbers
  return trialEnd.toISOString()
}

export const planOptions = (loginFromChannel) => {
  const sortedChannels = [
    ...(loginFromChannel ? [loginFromChannel] : []),
    ...['Bigcommerce', 'Shopify', 'Woocommerce', 'Amazon'].filter(c => c !== loginFromChannel),
  ].map(c => `b_${c}`)
  const channelStringArray = sortedChannels.reduce((acc, cur, index) => [
    ...acc,
    ...(index ? (
      (index === sortedChannels.length - 1) ? [' & ', cur] : [', ', cur]
    ) : [cur])
  ], [])
  return [
    {
      code: 'free',
      product: 'Free',
      icon: 'fa-th-large',
      tagline: 'Get started at no cost with core analytics for your ecommerce store',
      priceMonth: 0,
      priceYear: 0,
      priceTwoYear: 0,
      priceThreeYear: 0,
      description: [
        ['Unified Reporting Dashboard'],
        ['Fulfillment & shipping analytics'],
        ['Top SKUs & product performance'],
        ['Top campaigns & ad attribution'],
        ['Profit & margin reports'],
        ['Orders, refunds & revenue reports'],
        channelStringArray,
        ['Import all sales history'],
        ['b_300+ ', 'metrics to segment'],
        ['12-month data lookback'],
        ['10 users included'],
      ],
      limitOnModal: '$10,000/m',
    },
    {
      code: 'entrepreneur',
      product: 'Entrepreneur',
      icon: 'fa-rocket',
      tagline: 'Turn your store data into real growth — fast. Everything a growing business needs in one dashboard.',
      priceMonth: 20,
      priceYear: 192,
      priceTwoYear: 360,
      priceThreeYear: 504,
      priceLabel: 'per month',
      aiCredits: '100 AI credits / month',
      description: [
        ['Powerful ecommerce analytics dashboard'],
        ['Connect your store + marketing channels'],
        ['b_300+ ', 'metrics & customer segments'],
        ['Import your entire sales history'],
        ['Profit, margins & COGS reports'],
        ['Build custom reports & dashboards'],
        ['Share, schedule & export reports'],
        ['AI Chat for instant data insights'],
        ['Email marketing analytics'],
        ['12-month data lookback'],
        ['b_1 user included'],
      ],
      limit: '1 store + 1 standard integration',
      limitOnModal: '1 store + 1 standard integration',
    },
    {
      code: 'legacy-business',
      product: 'Legacy Business',
      hidden: true,
      priceMonth: 100,
      priceYear: 960,
      priceTwoYear: 1800,
      priceThreeYear: 2520,
      aiCredits: '1,000 AI credits / month',
      description: [
        ['Everything in Entrepreneur plus:'],
        ['Advanced filters & segments'],
        ['Schedule & email reports'],
        ['Sales team reports'],
        ['ShipStation integration'],
        ['Google Analytics integration'],
        ['Traffic & advertising analytics'],
        ['Inventory analytics'],
        ['Historical data: ', 'b_5+ years'],
        ['Priority support'],
      ],
      limit: 'Up to 50 users included',
      limitOnModal: '$200,000/m & up to 50 users',
    },
    {
      code: 'business',
      product: 'Business',
      icon: 'fa-chart-bar',
      tagline: 'All-in-one analytics for growing and established ecommerce businesses',
      aiCredits: '1,000 AI credits / month',
      description: [
        ['Everything in Entrepreneur, plus:'],
        ['Up to ', 'b_20 users'],
        ['Advanced filters & segments'],
        ['Schedule & email reports'],
        ['Sales team reports'],
        ['ShipStation integration'],
        ['Google Analytics integration'],
        ['Web traffic & cart conversion reporting'],
        ['Real-time traffic reports'],
        ['Inventory analytics'],
        ['Traffic & advertising analytics'],
        ['Historical data: ', 'b_5+ years'],
        ['Priority support'],
      ],
      limit: 'Up to 20 users included',
      limitOnModal: '$200,000/m & up to 20 users',
    },
    {
      code: 'enterprise',
      product: 'Enterprise',
      icon: 'fa-building',
      hidden: true,
      tagline: 'Full-scale platform with dedicated support, custom development, and strategic growth services',
      aiCredits: '1,000 AI credits / month',
      description: [
        ['Everything in Business, plus:'],
        ['Unlimited sales volume'],
        ['A/B testing & experimentation'],
        ['Dedicated hosting & infrastructure'],
        ['AI-powered development & automation'],
        ['CRM & ERP integrations'],
        ['Custom data pipelines'],
        ['Marketing optimization & growth strategies'],
        ['Advanced attribution modeling'],
        ['Custom metric building & pivots'],
        ['Google Sheets & Looker exports'],
        ['GDPR compliance tools'],
        ['Dedicated account manager'],
        ['Custom onboarding & training'],
        ['SLA & enterprise security'],
      ],
      limit: 'Unlimited users',
      limitOnModal: 'Unlimited users',
    },
  ]
}

export const planOptionNames = planOptions().map(o => o.product)

// Integration limits per plan (null = unlimited)
export const planIntegrationLimits = {
  Free: { stores: 1, integrations: null },
  Entrepreneur: { stores: 1, integrations: null },
  Professional: { stores: null, integrations: null }, // retired; kept for grandfathered subscribers
  'Legacy Business': { stores: null, integrations: null },
  Business: { stores: null, integrations: null },
  Enterprise: { stores: null, integrations: null },
}

// Channels classified as "store" integrations
export const storeChannels = [
  'Bigcommerce', 'Shopify', 'Woocommerce', 'Wix', 'Square',
  'Paypal Merchant', 'Amazon', 'Walmart', 'eBay', 'Etsy',
]

export const addOnOptions = ({ Traffic: trafficPrice, AI: aiPrice } = {}) => {
  return [
    {
      code: 'ai',
      product: 'AI',
      productTitle: 'AI Chat',
      requiredPlanLevel: 'Entrepreneur',
      priceMonth: aiPrice,
      priceYear: Math.round(aiPrice * 9.6),
      priceTwoYear: Math.round(aiPrice * 18),
      priceThreeYear: Math.round(aiPrice * 25.2),
      description: [
        ['Ask questions—AI creates custom reports instantly.'],
        ['Save time with fast summaries and smart insights.'],
        ['Spot trends, fix issues, and see how your data connects.']
      ],
    },
    {
      code: 'inventory',
      product: 'Inventory',
      productTitle: 'Inventory Analytics',
      requiredPlanLevel: 'Business',
      priceMonth: 100,
      priceYear: 960,
      priceTwoYear: 1800,
      priceThreeYear: 2520,
      description: [
        ['Track your Inventory levels, sell through-rate, and turn over.'],
        ['See your top performers and bottom performers.'],
        ['Know when to restock and track seasonal changes.'],
      ],
    },
    {
      code: 'traffic',
      product: 'Traffic',
      productTitle: 'Traffic & Advertising',
      requiredPlanLevel: 'Business',
      priceMonth: trafficPrice,
      priceYear: Math.round(trafficPrice * 9.6),
      priceTwoYear: Math.round(trafficPrice * 18),
      priceThreeYear: Math.round(trafficPrice * 25.2),
      description: [
        ['Know what campaigns have great ROAS and improve your funnel performance.'],
        ['Compare traffic from all your marketing efforts and know what landing pages are performing best.'],
        ['Get the detail your team needs.'],
      ],
    },
  ]
}

export const addOnOptionNames = planOptions().map(o => o.product)

// Credit descriptions for add-on cards (keyed by product name)
export const addOnCreditDescriptions = {
  AI: (priceMonth) => `${Math.round((priceMonth || 0) * 2)} AI credits / month`,
}

export const getMonthlyPriceFromStripePlan = (plan) => {
  let ret
  const intervalCount = Number(_.get(plan, ['metadata', 'intervalCount'], plan.interval_count))
  if (plan.interval === 'month') {
    ret = plan.amount
  }
  else if (intervalCount === 1) {
    ret = plan.amount / 9.6
  }
  else if (intervalCount === 2) {
    // ret = plan.amount / 18 // for 2-year
    ret = plan.amount / 9 // for 2 x yearly
  }
  else {
    // ret = plan.amount / 25.2 // for 3-year
    ret = plan.amount / 8.4 // for 3 x yearly
  }
  return Math.round(ret / 100)
}

export const getAffectedAddOns = (selectedPlanOption, stripeSubscriptionAddOns) => {
  const selectedPlanPrice = selectedPlanOption.interval === 'month' ? selectedPlanOption.priceMonth : (
    selectedPlanOption.intervalCount === 1 ? selectedPlanOption.priceYear : (
      selectedPlanOption.intervalCount ===  2 ? selectedPlanOption.priceTwoYear : selectedPlanOption.priceThreeYear
    )
  )
  const intervalText = selectedPlanOption.interval === 'month' ? 'm' : `${selectedPlanOption.intervalCount}y`
  const durationText = selectedPlanOption.interval === 'month' ? '1 Month' : `${selectedPlanOption.intervalCount} Year${selectedPlanOption.intervalCount > 1 ? 's' : ''}`
  const savingText = selectedPlanOption.interval === 'month' ? '' : (
    selectedPlanOption.intervalCount === 1 ? '(20% Savings)' : (
      selectedPlanOption.intervalCount ===  2 ? '(25% Savings)' : '(30% Savings)'
    )
  )
  const savingTextV2 = selectedPlanOption.interval === 'month' ? 'No Savings' : (
    selectedPlanOption.intervalCount === 1 ? '20% Savings' : (
      selectedPlanOption.intervalCount ===  2 ? '25% Savings' : '30% Savings'
    )
  )
  const billingTypeText = selectedPlanOption.interval === 'month' ? 'Monthly Billable' : 'Yearly Billable'
  const affectedAddOns = stripeSubscriptionAddOns.filter(addOn => 
    addOn.interval !== selectedPlanOption.interval ||
    Number(_.get(addOn, ['metadata', 'intervalCount'], addOn.interval_count)) !== selectedPlanOption.intervalCount
  ).map(affectedAddOn => {
    let affectedAddOnPrice
    const monthlyAddOnPrice = getMonthlyPriceFromStripePlan(affectedAddOn)
    affectedAddOnPrice = monthlyAddOnPrice * (
      selectedPlanOption.interval === 'month' ? 1 : (
        selectedPlanOption.intervalCount === 1 ? 9.6 : (
          selectedPlanOption.intervalCount === 2 ? 18 : 25.2
        )
      )
    )
    const monthlyPaidPrice = _.round(affectedAddOnPrice / (
      selectedPlanOption.interval === 'month' ? 1 : (selectedPlanOption.intervalCount * 12)
    ), 2)
    const summaryText = selectedPlanOption.interval === 'month' ? 
      `Monthly $${numeral(monthlyPaidPrice).format('0,0.0').replace(/\.0$/, '').replace(/\.00$/, '')}/m` : 
      `Yearly $${numeral(monthlyPaidPrice).format('0,0.0').replace(/\.0$/, '').replace(/\.00$/, '')}/m billed, $${numeral(affectedAddOnPrice).format('0,0')}/${intervalText} ${savingText}`
    const confirmationText = selectedPlanOption.interval === 'month' ? 
    `Monthly $${numeral(monthlyPaidPrice).format('0,0.00').replace(/\.0$/, '').replace(/\.00$/, '')}/m` : 
    `Yearly $${numeral(affectedAddOnPrice).format('0,0.00').replace(/\.0$/, '').replace(/\.00$/, '')}/${intervalText}`
    
    return {
      ...affectedAddOn,
      monthlyPaidPrice,
      calculatedPrice: affectedAddOnPrice,
      summaryText,
      confirmationText,
      durationText,
      savingTextV2,
      billingTypeText,
    }
  })
  return affectedAddOns
}